import { Check } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { FunctionalGroup } from "@types";
import { TaskProgram, TrainingScope } from "src/services/backend";

type TrainingProgramMatrixProps = {
  programs: TaskProgram[];
  functionalGroups: FunctionalGroup[];
  isLoading: boolean;
  scope: TrainingScope;
  onSelectProgram: (programId: string) => void;
};

const NAME_COLUMN = "minmax(220px, 280px)";
const CELL_HEIGHT = 44;

export const TrainingProgramMatrix = ({
  programs,
  functionalGroups,
  isLoading,
  scope,
  onSelectProgram,
}: TrainingProgramMatrixProps) => {
  if (isLoading) return null;

  if (programs.length === 0) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {scope === "mine"
            ? "No training programs assigned to you yet."
            : "No training programs yet. Create one to start assigning functional groups."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: "auto", maxHeight: 520 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            functionalGroups.length === 0
              ? NAME_COLUMN
              : `${NAME_COLUMN} repeat(${functionalGroups.length}, minmax(96px, 1fr))`,
          minWidth: "fit-content",
        }}
      >
        <Box
          sx={{
            gridColumn: "1 / 2",
            position: "sticky",
            top: 0,
            left: 0,
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1,
            minHeight: CELL_HEIGHT,
            borderBottom: 1,
            borderRight: 1,
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Training Program
          </Typography>
        </Box>
        {functionalGroups.map((group) => (
          <Box
            key={`head-${group.id}`}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1,
              py: 1,
              minHeight: CELL_HEIGHT,
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                width: "100%",
                textAlign: "center",
                fontWeight: 600,
                lineHeight: 1.25,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {group.name}
            </Typography>
          </Box>
        ))}

        {programs.map((program, rowIndex) => {
          const isLastRow = rowIndex === programs.length - 1;
          const rowBorder = isLastRow ? 0 : 1;
          const assignedGroupIds = new Set(
            program.assignments.map(
              ({ functionalGroupId }) => functionalGroupId
            )
          );
          return (
            <Box key={`row-${program.id}`} sx={{ display: "contents" }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => onSelectProgram(program.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectProgram(program.id);
                  }
                }}
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  px: 2,
                  minHeight: CELL_HEIGHT,
                  borderBottom: rowBorder,
                  borderRight: 1,
                  borderColor: "divider",
                  borderBottomLeftRadius: isLastRow ? 1 : 0,
                  borderBottomRightRadius:
                    isLastRow && functionalGroups.length === 0 ? 1 : 0,
                  backgroundColor: "background.paper",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {program.name}
                </Typography>
              </Box>

              {functionalGroups.map((group) => (
                <Box
                  key={`cell-${program.id}-${group.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: CELL_HEIGHT,
                    borderBottom: rowBorder,
                    borderColor: "divider",
                    borderBottomRightRadius:
                      isLastRow &&
                      group.id ===
                        functionalGroups[functionalGroups.length - 1].id
                        ? 1
                        : 0,
                  }}
                >
                  {assignedGroupIds.has(group.id) && (
                    <Check fontSize="small" color="success" />
                  )}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
